import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

export default function Login() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [firm, setFirm] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);

  async function handleSignIn(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) setError(error.message);
    setLoading(false);
  }

  async function handleReset(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: 'https://doer-seller.com/reset-password',
    });
    if (error) {
      setError(error.message);
    } else {
      setResetSent(true);
    }
    setLoading(false);
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!inviteCode.trim()) { setError('An invite code is required to join.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    const { error } = await signUp(email, password, name, firm, inviteCode);
    if (error) {
      setError(error.message);
    } else {
      setSuccess('Account created! Check your email to confirm your address, then sign in.');
      setMode('signin');
    }
    setLoading(false);
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-label">Stimulyst</div>
          <div className="login-brand-product">doer-seller</div>
          <div className="login-brand-tagline">Corporate Client CRM</div>
        </div>

        {/* Mode switcher */}
        <div style={{
          display: 'flex',
          background: 'var(--gray1)',
          borderRadius: 8,
          padding: 3,
          marginBottom: 20,
          gap: 3
        }}>
          <button
            onClick={() => { setMode('signin'); setError(''); setSuccess(''); }}
            style={{
              flex: 1, padding: '7px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 600,
              background: mode === 'signin' ? 'var(--navy)' : 'transparent',
              color: mode === 'signin' ? '#fff' : 'var(--text2)',
              transition: 'all 0.15s'
            }}
          >Sign In</button>
          <button
            onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}
            style={{
              flex: 1, padding: '7px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 600,
              background: mode === 'signup' ? 'var(--navy)' : 'transparent',
              color: mode === 'signup' ? '#fff' : 'var(--text2)',
              transition: 'all 0.15s'
            }}
          >Join (Invite Only)</button>
        </div>

        {error && <div className="login-error">{error}</div>}
        {success && (
          <div style={{
            background: 'rgba(45,125,58,0.08)', border: '1px solid rgba(45,125,58,0.2)',
            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
            fontSize: 13, color: 'var(--success)', fontFamily: 'Poppins, sans-serif', lineHeight: 1.5
          }}>{success}</div>
        )}

        {mode === 'signin' ? (
          <form onSubmit={handleSignIn}>
            <div className="fg">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@yourfirm.com" required autoFocus />
            </div>
            <div className="fg">
              <label>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" required />
            </div>
            <button type="submit" className="btn btn-navy"
              style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
              disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={() => { setResetMode(true); setError(''); setSuccess(''); setResetEmail(email); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--teal)', fontFamily: 'Poppins, sans-serif', textDecoration: 'underline' }}
              >Forgot password?</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSignUp}>
            <div style={{
              background: 'rgba(245,197,24,0.08)', border: '1px solid rgba(245,197,24,0.2)',
              borderLeft: '3px solid var(--gold)', borderRadius: 7,
              padding: '9px 12px', marginBottom: 14,
              fontSize: 12, color: 'var(--navy)', fontFamily: 'Poppins, sans-serif', lineHeight: 1.5
            }}>
              Access to doer-seller requires an invite code from Liz. If you're a Stimulyst Accelerator member, check your welcome email.
            </div>
            <div className="fg">
              <label>Invite Code</label>
              <input type="text" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Enter your invite code" required
                style={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }} />
            </div>
            <div className="fg2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label>Your Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="Jane Carter" required />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}>
                <label>Firm Name</label>
                <input type="text" value={firm} onChange={e => setFirm(e.target.value)}
                  placeholder="Carter Consulting" />
              </div>
            </div>
            <div className="fg" style={{ marginTop: 12 }}>
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@yourfirm.com" required />
            </div>
            <div className="fg">
              <label>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters" required />
            </div>
            <div className="fg">
              <label>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••" required />
            </div>
            <button type="submit" className="btn btn-gold"
              style={{ width: '100%', justifyContent: 'center', marginTop: 8, color: 'var(--navy)' }}
              disabled={loading}>
              {loading ? 'Creating account...' : 'Create My Account'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: 'var(--gray3)' }}>
          Powered by Stimulyst &copy; 2026
        </div>
      </div>

      {/* Forgot password modal */}
      {resetMode && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '32px 28px', width: 380,
            maxWidth: '95vw', boxShadow: '0 18px 50px rgba(0,0,0,0.2)'
          }}>
            {resetSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📬</div>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 17, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>Check your email</div>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 20 }}>
                  We sent a password reset link to <strong>{resetEmail}</strong>. Click the link in that email to set a new password.
                </div>
                <button className="btn btn-navy" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => { setResetMode(false); setResetSent(false); }}>
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 17, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>Reset your password</div>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: 13, color: 'var(--text2)', marginBottom: 18, lineHeight: 1.5 }}>
                  Enter your email and we'll send you a link to reset your password.
                </div>
                {error && <div className="login-error">{error}</div>}
                <form onSubmit={handleReset}>
                  <div className="fg">
                    <label>Email</label>
                    <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                      placeholder="you@yourfirm.com" required autoFocus />
                  </div>
                  <button type="submit" className="btn btn-teal"
                    style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                    disabled={loading}>
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button type="button" onClick={() => { setResetMode(false); setError(''); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--gray3)', fontFamily: 'Poppins, sans-serif' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
